import { SxProps, Theme } from "@mui/material";
import securityPoliciesTokens, {
  securityPoliciesResponsiveTokens as r,
} from "./SecurityPolicies.tokens";
import {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";

/** Same rhythm `SettingsLayout` puts between its own stacked cards. */
const shellPhone = settingsShellResponsiveTokens.phone;
const stackGap = {
  xs: shellPhone.contentGap.std,
  sm: settingsShellResponsiveTokens.tablet.contentGap,
};

const {
  card,
  header,
  toggle,
  rowToggle,
  stateLabel,
  expiryRow,
  expiryField,
  primaryButton,
  leadingIcon,
  sectionHeader,
  activityLink,
  allowlistCard,
  search,
  table,
  spinner,
} = securityPoliciesTokens;

/* --------------------------------------------------------------- card shell */

/** White rounded card shared by "Password Expiry" and the MFA banner. */
export const cardSx: SxProps<Theme> = {
  bgcolor: "background.paper",
  border: `${card.borderWidth} solid ${card.border}`,
  borderRadius: card.radius,
  boxShadow: card.shadow,
  boxSizing: "border-box",
  p: { xs: r.card.padding, md: card.padding },
  display: "flex",
  flexDirection: "column",
  gap: { xs: r.card.gap, md: card.gap },
};

/** Same chrome, but a single centred row (MFA card). */
export const rowCardSx: SxProps<Theme> = {
  bgcolor: "background.paper",
  border: `${card.borderWidth} solid ${card.border}`,
  borderRadius: card.radius,
  boxShadow: card.shadow,
  boxSizing: "border-box",
  p: { xs: r.card.padding, md: card.padding },
  display: "flex",
  alignItems: { xs: "flex-start", md: "center" },
  gap: leadingIcon.gap,
};

export const cardHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: header.gap,
};

export const cardHeaderTextSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: header.textGap,
  minWidth: 0,
};

/** MFA card stacks its two lines a touch tighter and must fill the row. */
export const rowCardTextSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: header.tightTextGap,
  minWidth: 0,
};

/** Loading variant of `cardHeaderTextSx` — grows so the lines fill the row. */
export const skeletonHeaderTextSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: header.textGap,
  minWidth: 0,
  flex: 1,
};

export const cardTitleSx: SxProps<Theme> = {
  color: header.titleColor,
  fontSize: { xs: r.header.titleFontSize, md: header.titleFontSize },
  fontWeight: header.titleFontWeight,
  letterSpacing: header.titleLetterSpacing,
  lineHeight: "normal",
};

export const cardDescriptionSx = (enforced = false): SxProps<Theme> => ({
  color: enforced
    ? header.descriptionEnforcedColor
    : header.descriptionColor,
  fontSize: {
    xs: r.header.descriptionFontSize,
    md: header.descriptionFontSize,
  },
  lineHeight: header.descriptionLineHeight,
});

/* ------------------------------------------------------------------ toggles */

export const toggleWrapperSx: SxProps<Theme> = {
  ml: "auto",
  mt: toggle.marginTop,
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
};

/** Right-hand cluster of the MFA / allowlist banners: caps label + toggle. */
export const toggleGroupSx: SxProps<Theme> = {
  ml: "auto",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  gap: stateLabel.gap,
};

export const stateLabelSx: SxProps<Theme> = {
  fontSize: stateLabel.fontSize,
  fontWeight: stateLabel.fontWeight,
  letterSpacing: stateLabel.letterSpacing,
  color: stateLabel.color,
  lineHeight: "normal",
};

export const toggleTrackSx = (checked: boolean): SxProps<Theme> => ({
  position: "relative",
  flexShrink: 0,
  width: toggle.width,
  height: toggle.height,
  p: 0,
  borderRadius: "999px",
  bgcolor: checked ? toggle.trackOn : toggle.trackOff,
  transition: toggle.transition,
  "&:hover": { bgcolor: checked ? toggle.trackOn : toggle.trackOff },
  "&.Mui-disabled": { bgcolor: checked ? toggle.trackOn : toggle.trackOff },
  "&.Mui-focusVisible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: "2px",
  },
});

export const toggleKnobSx = (checked: boolean): SxProps<Theme> => ({
  position: "absolute",
  top: toggle.knobTop,
  left: 0,
  width: toggle.knobSize,
  height: toggle.knobSize,
  borderRadius: "50%",
  bgcolor: toggle.knobBg,
  boxShadow: toggle.knobShadow,
  transform: checked ? toggle.knobOffsetOn : toggle.knobOffsetOff,
  transition: toggle.knobTransition,
});

export const rowToggleTrackSx = (checked: boolean): SxProps<Theme> => ({
  position: "relative",
  flexShrink: 0,
  width: rowToggle.width,
  height: rowToggle.height,
  p: 0,
  borderRadius: "999px",
  bgcolor: checked ? toggle.trackOn : toggle.trackOff,
  transition: toggle.transition,
  "&:hover": { bgcolor: checked ? toggle.trackOn : toggle.trackOff },
  "&.Mui-disabled": { bgcolor: checked ? toggle.trackOn : toggle.trackOff },
  "&.Mui-focusVisible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: "2px",
  },
});

export const rowToggleKnobSx = (checked: boolean): SxProps<Theme> => ({
  position: "absolute",
  top: rowToggle.knobTop,
  left: 0,
  width: rowToggle.knobSize,
  height: rowToggle.knobSize,
  borderRadius: "50%",
  bgcolor: toggle.knobBg,
  boxShadow: toggle.knobShadow,
  transform: checked ? rowToggle.knobOffsetOn : rowToggle.knobOffsetOff,
  transition: toggle.knobTransition,
});

export const spinnerSx: SxProps<Theme> = {
  color: spinner.color,
  flexShrink: 0,
};

/* ---------------------------------------------------- password expiry block */

/** Wrapper around the "… expire in [ n ] days" row; dims when disabled. */
export const expiryRowStackSx = (enabled: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  gap: expiryRow.gap,
  opacity: enabled ? 1 : expiryRow.disabledOpacity,
  transition: expiryRow.opacityTransition,
});

export const expiryRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: expiryRow.gap,
};

export const expiryLabelSx: SxProps<Theme> = {
  // The reference lets the label take the free space; desktop keeps its column.
  flex: { xs: 1, md: "none" },
  width: expiryRow.labelWidth,
  flexShrink: 0,
  color: expiryRow.labelColor,
  fontSize: expiryRow.fontSize,
  fontWeight: expiryRow.fontWeight,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  maxWidth: "none",
  overflow: "visible",
  p: 0,
  m: 0,
};

export const expirySuffixSx: SxProps<Theme> = {
  width: { xs: r.expiry.suffixWidth, md: "auto" },
  color: expiryRow.suffixColor,
  fontSize: expiryRow.fontSize,
  fontWeight: expiryRow.suffixFontWeight,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  p: 0,
  m: 0,
};

/**
 * 72×36 centred box — the design's inline day input. The error message renders
 * below the row (the box is far too narrow for it), so an invalid value only
 * recolours the border here.
 */
export const expiryFieldSx = (hasError: boolean): SxProps<Theme> => ({
  width: expiryField.width,
  flexShrink: 0,
  "& .MuiOutlinedInput-root": {
    height: expiryField.height,
    p: 0,
    borderRadius: expiryField.radius,
    bgcolor: expiryField.bg,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: hasError ? "error.main" : expiryField.border,
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: hasError ? "error.main" : expiryField.border,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: hasError ? "error.main" : expiryField.focusBorder,
      borderWidth: "1px",
    },
    "&.Mui-disabled": {
      bgcolor: expiryField.bg,
      "& .MuiOutlinedInput-notchedOutline": { borderColor: expiryField.border },
    },
  },
  "& .MuiOutlinedInput-input": {
    height: "100%",
    boxSizing: "border-box",
    p: `0 ${expiryField.paddingX}`,
    fontSize: expiryField.fontSize,
    fontWeight: expiryField.fontWeight,
    color: expiryField.color,
    "&.Mui-disabled": {
      WebkitTextFillColor: expiryField.color,
      color: expiryField.color,
    },
  },
});

export const fieldErrorSx: SxProps<Theme> = {
  ml: 0,
  mt: 0,
  fontSize: expiryRow.errorFontSize,
};

export const buttonRowSx = (enabled: boolean): SxProps<Theme> => ({
  // The action is wrapped in a Tooltip span, so the row has to stretch it for
  // the button's own `width: 100%` to reach the card edges.
  "& > *": { width: { xs: "100%", md: "auto" } },
  display: "flex",
  opacity: enabled ? 1 : expiryRow.disabledOpacity,
  transition: expiryRow.opacityTransition,
});

export const primaryButtonSx: SxProps<Theme> = {
  // Both small references run the primary action full width at 44px.
  width: { xs: "100%", md: "auto" },
  height: primaryButton.height,
  minWidth: 0,
  px: primaryButton.paddingX,
  py: 0,
  borderRadius: primaryButton.radius,
  bgcolor: primaryButton.bg,
  color: primaryButton.color,
  fontSize: primaryButton.fontSize,
  fontWeight: primaryButton.fontWeight,
  lineHeight: "normal",
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  flexShrink: 0,
  "&:hover": { bgcolor: primaryButton.bg, boxShadow: "none" },
  "&.Mui-disabled": {
    bgcolor: primaryButton.disabledBg,
    color: primaryButton.disabledColor,
    cursor: "not-allowed",
    pointerEvents: "auto",
  },
};

export const buttonSpinnerSx: SxProps<Theme> = {
  color: "inherit",
  mr: primaryButton.spinnerGap,
};

/* ------------------------------------------------------------------ MFA card */

export const leadingIconSx: SxProps<Theme> = {
  width: leadingIcon.size,
  height: leadingIcon.size,
  borderRadius: leadingIcon.radius,
  bgcolor: leadingIcon.bg,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

/* ------------------------------------------------------- whitelisting header */

/**
 * The design nudges this row 4px further from the card above it. It is applied
 * as padding, not margin, because the shell's `Stack` owns the 20px rhythm
 * between blocks and would override a margin.
 */
/**
 * "Whitelisting IPs · Activity Log · + Add Whitelist IP". Both small references
 * keep the heading and the log link on one line and drop the action to its own
 * full-width row underneath.
 */
export const sectionHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  rowGap: { xs: r.card.gap, md: 0 },
  columnGap: sectionHeader.gap,
  pt: sectionHeader.marginTop,
  "& > .MuiButton-contained": {
    width: { xs: "100%", md: "auto" },
    flexBasis: { xs: "100%", md: "auto" },
  },
};

export const sectionTitleSx: SxProps<Theme> = {
  fontSize: sectionHeader.fontSize,
  fontWeight: sectionHeader.fontWeight,
  letterSpacing: sectionHeader.letterSpacing,
  color: sectionHeader.color,
  lineHeight: "normal",
};

export const activityLinkSx: SxProps<Theme> = {
  ml: "auto",
  minWidth: 0,
  p: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: activityLink.gap,
  bgcolor: "transparent",
  color: activityLink.color,
  fontSize: activityLink.fontSize,
  fontWeight: activityLink.fontWeight,
  lineHeight: "normal",
  textTransform: "none",
  whiteSpace: "nowrap",
  flexShrink: 0,
  "& .MuiButton-startIcon": { m: 0 },
  "&:hover": { bgcolor: "transparent", color: activityLink.hoverColor },
};

/* --------------------------------------------------------- allowlist banner */

/**
 * Shadowless card carrying a 3px brand stripe down its left edge — the design
 * draws the stripe as the card's own left border.
 */
export const allowlistCardSx: SxProps<Theme> = {
  bgcolor: "background.paper",
  border: `${card.borderWidth} solid ${allowlistCard.border}`,
  borderLeft: `${allowlistCard.stripeWidth} solid ${allowlistCard.stripeColor}`,
  borderRadius: allowlistCard.radius,
  boxSizing: "border-box",
  p: allowlistCard.padding,
  display: "flex",
  alignItems: "center",
  gap: allowlistCard.gap,
};

export const allowlistIconSx: SxProps<Theme> = {
  // The reference drops the shield tile and leans on the brand stripe alone.
  display: { xs: "none", md: "flex" },
  width: allowlistCard.iconSize,
  height: allowlistCard.iconSize,
  borderRadius: allowlistCard.iconRadius,
  bgcolor: allowlistCard.iconBg,
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const allowlistTextSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: allowlistCard.textGap,
  minWidth: 0,
};

export const allowlistTitleSx: SxProps<Theme> = {
  fontSize: allowlistCard.titleFontSize,
  fontWeight: allowlistCard.titleFontWeight,
  color: allowlistCard.titleColor,
  lineHeight: "normal",
};

export const allowlistNoteSx = (enforced: boolean): SxProps<Theme> => ({
  fontSize: allowlistCard.noteFontSize,
  color: enforced ? allowlistCard.noteOnColor : allowlistCard.noteOffColor,
  lineHeight: "normal",
});

/* -------------------------------------------------------------- table card */

export const tableCardSx: SxProps<Theme> = {
  /*
    Below `md` the reference drops the wrapper: the search box and every
    allowlist entry stand on the page canvas as their own blocks.
  */
  bgcolor: { xs: "transparent", md: "background.paper" },
  border: {
    xs: "none",
    md: `${card.borderWidth} solid ${card.border}`,
  },
  borderRadius: { xs: 0, md: card.radius },
  boxShadow: { xs: "none", md: card.shadow },
  overflow: { xs: "visible", md: "hidden" },
  display: { xs: "flex", md: "block" },
  flexDirection: "column",
  gap: stackGap,
  [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
};

/** Strip above the column header hosting the search box. */
export const searchBarSx: SxProps<Theme> = {
  p: { xs: 0, md: search.padding },
  borderBottom: {
    xs: "none",
    md: `${card.borderWidth} solid ${search.borderBottom}`,
  },
};

/** Overrides turning the shared settings search box into the design's
 *  38px, tinted in-card variant. Width comes from the `width` prop so it does
 *  not collide with the component's own responsive width rule. */
export const searchFieldSx: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    height: search.height,
    minHeight: search.height,
    borderRadius: search.radius,
    backgroundColor: search.bg,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: search.border },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: search.border,
  },
};

/** Horizontal scroller: the five fixed columns stop fitting on narrow viewports. */
export const tableScrollSx: SxProps<Theme> = {
  overflowX: { xs: "visible", md: "auto" },
  display: { xs: "contents", md: "block" },
};

export const tableInnerSx: SxProps<Theme> = {
  minWidth: { xs: 0, md: table.minWidth },
  display: { xs: "contents", md: "block" },
};

export const columnHeaderSx: SxProps<Theme> = {
  // Five fixed tracks the phone cannot carry; entries become cards below `md`.
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: table.columns,
  px: table.gutter,
  py: table.headerPaddingY,
  bgcolor: table.headerBg,
  borderBottom: `${card.borderWidth} solid ${table.headerBorder}`,
  fontSize: table.headerFontSize,
  fontWeight: table.headerFontWeight,
  letterSpacing: table.headerLetterSpacing,
  color: table.headerColor,
  lineHeight: "normal",
};

/** A single allowlist row. The last row drops its divider. */
export const rowSx = (isLast: boolean): SxProps<Theme> => ({
  display: "grid",
  gridTemplateColumns: table.columns,
  alignItems: "center",
  px: table.gutter,
  py: table.rowPaddingY,
  borderBottom: isLast
    ? "none"
    : `${card.borderWidth} solid ${table.rowDivider}`,
  fontSize: table.rowFontSize,
  color: table.rowColor,
  lineHeight: "normal",
});

export const labelCellSx: SxProps<Theme> = {
  fontWeight: table.labelFontWeight,
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const valueCellSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: table.valueGap,
  minWidth: 0,
  color: table.valueColor,
  fontFamily: table.valueFontFamily,
  fontSize: table.valueFontSize,
};

export const valueDotSx = (enabled: boolean): SxProps<Theme> => ({
  width: table.dotSize,
  height: table.dotSize,
  borderRadius: "50%",
  bgcolor: enabled ? table.dotOn : table.dotOff,
  flexShrink: 0,
});

export const valueTextSx: SxProps<Theme> = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const statusCellSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: table.statusGap,
};

export const statusTextSx = (enabled: boolean): SxProps<Theme> => ({
  fontSize: table.statusFontSize,
  fontWeight: table.statusFontWeight,
  color: enabled ? table.statusActiveColor : table.statusInactiveColor,
  lineHeight: "normal",
});

export const addedByCellSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: table.addedByGap,
  minWidth: 0,
};

export const addedByTextSx: SxProps<Theme> = {
  fontSize: table.addedByFontSize,
  fontWeight: table.addedByFontWeight,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const addedOnTextSx: SxProps<Theme> = {
  fontSize: table.addedOnFontSize,
  color: table.addedOnColor,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

/** Row overflow trigger — the design draws it as a bold "⋮" glyph. */
export const actionButtonSx: SxProps<Theme> = {
  font: "inherit",
  display: "block",
  width: "100%",
  p: 0,
  m: 0,
  border: "none",
  bgcolor: "transparent",
  textAlign: "right",
  color: table.actionColor,
  fontWeight: table.actionFontWeight,
  letterSpacing: table.actionLetterSpacing,
  lineHeight: "normal",
  cursor: "pointer",
  "&:hover": { color: table.rowColor },
  "&:focus-visible": {
    outline: `2px solid ${primaryButton.bg}`,
    outlineOffset: "2px",
    borderRadius: "3px",
  },
};

/** Placeholder shown in place of rows when no entry matches. */
export const emptyStateSx: SxProps<Theme> = {
  px: { xs: r.entryCard.padding, md: table.gutter },
  py: table.emptyPaddingY,
  textAlign: "center",
  fontSize: table.emptyFontSize,
  color: table.emptyColor,
  lineHeight: "normal",
};

export const rowMenuItemSx: SxProps<Theme> = {
  fontSize: table.valueFontSize,
  fontWeight: table.labelFontWeight,
  color: table.rowColor,
};

export const rowMenuDangerItemSx: SxProps<Theme> = {
  fontSize: table.valueFontSize,
  fontWeight: table.labelFontWeight,
  color: "error.main",
};

/* ---------------------------------------------------------------- card list */

/**
 * Below `md` an allowlist entry is a card: its label, the address and who added
 * it stacked, with the row switch and overflow trigger trailing.
 */
export const entryCardSx: SxProps<Theme> = {
  display: "flex",
  boxSizing: "border-box",
  alignItems: "center",
  gap: r.entryCard.gap,
  p: r.entryCard.padding,
  bgcolor: "background.paper",
  border: `${card.borderWidth} solid ${card.border}`,
  borderRadius: card.radius,
};

export const entryCardTextSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: r.entryCard.textGap,
};

export const entryCardLabelSx: SxProps<Theme> = {
  fontSize: r.entryCard.labelFontSize,
  fontWeight: table.labelFontWeight,
  color: table.rowColor,
  lineHeight: "normal",
  wordBreak: "break-word",
};

export const entryCardValueSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: table.valueGap,
  minWidth: 0,
  fontFamily: table.valueFontFamily,
  fontSize: r.entryCard.valueFontSize,
  color: table.valueColor,
  lineHeight: "normal",
};

export const entryCardAddedBySx: SxProps<Theme> = {
  fontSize: r.entryCard.addedByFontSize,
  color: table.addedOnColor,
  lineHeight: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** Row switch + overflow trigger, trailing the card. */
export const entryCardActionsSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: table.statusGap,
  flexShrink: 0,
};

export const entryCardActionButtonSx: SxProps<Theme> = {
  font: "inherit",
  flexShrink: 0,
  p: 0,
  m: 0,
  border: "none",
  bgcolor: "transparent",
  color: table.actionColor,
  fontWeight: table.actionFontWeight,
  letterSpacing: table.actionLetterSpacing,
  lineHeight: "normal",
  cursor: "pointer",
  "&:hover": { color: table.rowColor },
  "&:focus-visible": {
    outline: `2px solid ${primaryButton.bg}`,
    outlineOffset: "2px",
    borderRadius: "3px",
  },
};
